import { List, fromJS } from 'immutable'
import { createSelector } from 'reselect'

export const MAX_SNAPSHOTS_PER_VEHICLE = 3
export const MAX_SNAPSHOTS_PER_PROFILE = 25

const EMPTY_LIST = List()

const getEntry = (state, { serialNumber }) =>
  state.getIn(['vehicleSnapshots', 'bySerialNumber', serialNumber])

const getBySerialNumber = (state) =>
  state.getIn(['vehicleSnapshots', 'bySerialNumber'])

export const getVehicleSnapshots = createSelector(getEntry, (entry) =>
  entry ? entry.get('list', EMPTY_LIST).toJS() : []
)

export const getVehicleSnapshotCount = createSelector(
  getVehicleSnapshots,
  (list) => list.length
)

export const getVehicleSnapshotsLoading = createSelector(getEntry, (entry) =>
  entry ? entry.get('loading', false) : false
)

export const getVehicleSnapshotsSaving = createSelector(getEntry, (entry) =>
  entry ? entry.get('saving', false) : false
)

export const getVehicleSnapshotsError = createSelector(getEntry, (entry) =>
  entry ? entry.get('error', null) : null
)

export const getProfileSnapshotCount = createSelector(
  getBySerialNumber,
  (bySerialNumber) => {
    if (!bySerialNumber) return 0
    return bySerialNumber.reduce(
      (total, entry) =>
        total + (entry.get('list') ? entry.get('list').size : 0),
      0
    )
  }
)

export const isAtVehicleLimit = createSelector(
  getVehicleSnapshotCount,
  (count) => count >= MAX_SNAPSHOTS_PER_VEHICLE
)

export const isAtProfileLimit = createSelector(
  getProfileSnapshotCount,
  (count) => count >= MAX_SNAPSHOTS_PER_PROFILE
)

export const getDeletingSnapshotIds = createSelector(getEntry, (entry) =>
  entry ? entry.get('deletingIds', fromJS([])).toJS() : []
)

export const getVehicleAsBuilt = createSelector(getEntry, (entry) => {
  if (!entry || !entry.get('asBuilt')) return null
  const asBuilt = entry.get('asBuilt')
  const pto = asBuilt.get('pto')
  const pneumatics = asBuilt.get('pneumatics')
  const switchAndGauge = asBuilt.get('switchAndGauge')
  const dataBookCodes = asBuilt.get('dataBookCodes')
  return {
    features: asBuilt.get('features', EMPTY_LIST).toJS(),
    buildDate: asBuilt.get('buildDate', null),
    loading: asBuilt.get('loading', false),
    error: asBuilt.get('error', null),
    pto: pto ? pto.toJS() : null,
    pneumatics: pneumatics ? pneumatics.toJS() : null,
    switchAndGauge: switchAndGauge ? switchAndGauge.toJS() : null,
    dashboardType: asBuilt.get('dashboardType', null),
    vehicleSubType: asBuilt.get('vehicleSubType', null),
    vehicleType: asBuilt.get('vehicleType', null),
    dataBookCodes: dataBookCodes ? dataBookCodes.toJS() : null,
    isPreProduction: asBuilt.get('isPreProduction', false),
  }
})

export const getAllVehiclesSnapshotData = createSelector(
  getBySerialNumber,
  (bySerialNumber) => {
    if (!bySerialNumber) return []
    return bySerialNumber
      .entrySeq()
      .map(([serialNumber, entry]) => ({
        serialNumber,
        snapshots: entry.get('list', fromJS([])).toJS(),
        loading: entry.get('loading', false),
        deletingIds: entry.get('deletingIds', fromJS([])).toJS(),
        error: entry.get('error', null),
      }))
      .toArray()
      .sort((a, b) => a.serialNumber.localeCompare(b.serialNumber))
  }
)
